/**
 * What a paper IS, to somebody browsing the library.
 *
 * One definition, because three apps render it and a fourth queries by it. The
 * kind is not stored: it is read from the two dimensions that already say it,
 * and deriving it in each app is how they come to disagree.
 *
 *     purpose   gives the family      exam-paper | topic-exercise
 *     pyqType   splits that family    FullPYP | MixedPYP | None
 *
 * The split is symmetric, and it earns its place both times. A past paper and a
 * mock are both `exam-paper` and only the PYQ claim tells them apart. A
 * **Topicwise PYQ** and a plain chapter exercise are both `topic-exercise`, and
 * to a student preparing for JEE those are not the same thing at all — one is a
 * chapter's worth of real exam questions, which is the reason they opened it.
 *
 * LIBRARY CONTENT ONLY. `custom` (a teacher's or student's own paper), `duel`
 * and `daily-plan` return `undefined` — they are not discoverable, they reach a
 * student through a grant or a flow, and every surface that shows them already
 * says so in its own name. A badge reading "Assignment" on the Assigned page is
 * noise, and inventing a label for a caller that does not exist is how a stale
 * vocabulary starts.
 *
 * See docs/FeatureDesigns/assignments-tests/qset-model-target.md §2.
 */

export type QuestionSetKind = 'past-paper' | 'mock-test' | 'topicwise-pyq' | 'topic-exercise';

/**
 * What a student reads. The enum is for code; these are for people.
 *
 * Two forms, because the same kind is read in two grammatical positions and one
 * string cannot serve both: a TAB names a shelf ("Previous Year Papers") and a
 * BADGE names the single row it sits on ("Previous Year Paper"). Naïvely adding
 * an "s" gets "Topicwise PYQs" right and "Previous Year Paperss" wrong, so the
 * plural is written out rather than derived.
 */
export const QUESTION_SET_KIND_LABELS: Record<QuestionSetKind, string> = {
    'past-paper': 'Previous Year Paper',
    'mock-test': 'Mock Test',
    'topicwise-pyq': 'Topicwise PYQ',
    'topic-exercise': 'Topic Exercise',
};

/** The shelf, for tabs, filter options and empty states. */
export const QUESTION_SET_KIND_LABELS_PLURAL: Record<QuestionSetKind, string> = {
    'past-paper': 'Previous Year Papers',
    'mock-test': 'Mock Tests',
    'topicwise-pyq': 'Topicwise PYQs',
    'topic-exercise': 'Topic Exercises',
};

/** In the order a filter should offer them: whole papers first, then chapters. */
export const QUESTION_SET_KINDS: QuestionSetKind[] = [
    'past-paper', 'mock-test', 'topicwise-pyq', 'topic-exercise',
];

/**
 * The kind of a paper, or `undefined` when it is not library content.
 *
 * `pyqType` is creator-**asserted** and never derived, so an absent one means
 * "nobody claimed this is a past-paper question" — which is treated as `None`
 * rather than guessed at.
 */
export function kindOf(set: {
    purpose?: string | null;
    pyqType?: string | null;
}): QuestionSetKind | undefined {
    if (set.purpose === 'exam-paper') {
        return set.pyqType === 'FullPYP' ? 'past-paper' : 'mock-test';
    }
    if (set.purpose === 'topic-exercise') {
        return set.pyqType === 'MixedPYP' ? 'topicwise-pyq' : 'topic-exercise';
    }
    return undefined;
}

/**
 * The list-request filters that select one kind.
 *
 * The inverse of `kindOf`, and kept beside it so the two cannot drift: a filter
 * that selects a different set of papers from the one the badge names is worse
 * than no filter, because both look right.
 */
export function kindQuery(kind: QuestionSetKind): { purpose: string; pyqType: string } {
    switch (kind) {
        case 'past-paper': return { purpose: 'exam-paper', pyqType: 'FullPYP' };
        case 'mock-test': return { purpose: 'exam-paper', pyqType: 'not-FullPYP' };
        case 'topicwise-pyq': return { purpose: 'topic-exercise', pyqType: 'MixedPYP' };
        case 'topic-exercise': return { purpose: 'topic-exercise', pyqType: 'not-MixedPYP' };
    }
}

/** Every purpose the library shows. What the All view asks for. */
export const LIBRARY_PURPOSES = ['exam-paper', 'topic-exercise'] as const;
